import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatTableModule } from '@angular/material/table';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiService } from '../../shared/services/api.service';
import { take } from 'rxjs';

// Confirmed against a real response from admin/get-fallback-analytics-overview
// (2026-07-22): the payload is `{ data: {...}, message }`, not the
// `{ statusCode, body: {...} }` wrapper this widget originally guessed.
interface FallbackAnalyticsTimeRange {
  startDate: string;
  endDate: string;
  daysAnalyzed: number;
}

interface FallbackAnalyticsVoiceSessions {
  total: number;
  avgDuration: number; // ms
  avgMessagesPerSession: number;
  avgKeepalivesPerSession: number;
}

interface FallbackAnalyticsFallbackMetrics {
  totalFallbacks: number;
  fallbackRate: string; // e.g. "30.0%"
  voiceOnlyRate: string;
  avgTimeToFallback: number; // ms
  avgTimeToFallbackSeconds: number;
}

interface FallbackAnalyticsDistribution {
  voiceToTriage: number;
  triageToImage: number;
  triageToText: number;
  imageToText: number;
  voiceDirectToText: number;
}

interface BreakdownEntry {
  total?: number;
  // hourlyDistribution entries use this key instead of `total` (confirmed
  // against a real response, 2026-07-22) — productTypeBreakdown/brandBreakdown
  // use `total`. Both are read defensively in normalizeBreakdown().
  voiceSessions?: number;
  fallbacks: number;
  fallbackRate: string;
}

interface FallbackAnalyticsQualityMetrics {
  sessionsWithKeepalives: number;
  keepaliveRate: string;
  avgKeepalivesInFalledSessions: number;
  avgKeepalivesInCompletedSessions: number;
}

interface FallbackAnalyticsData {
  timeRange: FallbackAnalyticsTimeRange;
  voiceSessions: FallbackAnalyticsVoiceSessions;
  fallbackMetrics: FallbackAnalyticsFallbackMetrics;
  fallbackDistribution: FallbackAnalyticsDistribution;
  productTypeBreakdown: Record<string, BreakdownEntry>;
  brandBreakdown: Record<string, BreakdownEntry>;
  // Despite the name, real data is keyed by date ("2026-07-20"), not an
  // hour-of-day bucket — labels are rendered as-is rather than assuming
  // either format.
  hourlyDistribution: Record<string, BreakdownEntry>;
  qualityMetrics: FallbackAnalyticsQualityMetrics;
}

interface FallbackAnalyticsResponseBody {
  data: FallbackAnalyticsData;
  message?: string;
}

interface FallbackAnalyticsResponse {
  statusCode: number;
  body: FallbackAnalyticsResponseBody;
}

interface BreakdownRow {
  label: string;
  total: number;
  fallbacks: number;
  fallbackRatePct: number;
  isOutlier: boolean;
}

interface DistributionRow {
  label: string;
  count: number;
}

type InsightSeverity = 'warning' | 'info';

interface Insight {
  severity: InsightSeverity;
  title: string;
  description: string;
  action: string;
}

const HIGH_FALLBACK_THRESHOLD = 45;
const HIGH_KEEPALIVE_THRESHOLD = 10;
const LOW_KEEPALIVE_THRESHOLD = 5;
const OUTLIER_SPREAD_THRESHOLD = 20;
const MIN_SESSIONS_FOR_OUTLIER = 3;

const FALLBACK_DISTRIBUTION_LABELS: Record<
  keyof FallbackAnalyticsDistribution,
  string
> = {
  voiceDirectToText: 'Voice → Text (direct)',
  voiceToTriage: 'Voice → Triage',
  triageToImage: 'Triage → Image',
  triageToText: 'Triage → Text',
  imageToText: 'Image → Text',
};

function parsePercent(value: string | number | undefined): number {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  const parsed = parseFloat(value.replace('%', '').trim());
  return isNaN(parsed) ? 0 : parsed;
}

function normalizeBreakdown(
  record: Record<string, BreakdownEntry> | undefined
): BreakdownRow[] {
  const rows = Object.entries(record || {}).map(([label, entry]) => ({
    label,
    total: entry?.total ?? entry?.voiceSessions ?? 0,
    fallbacks: entry?.fallbacks || 0,
    fallbackRatePct: parsePercent(entry?.fallbackRate),
    isOutlier: false,
  }));

  const eligible = rows.filter((r) => r.total >= MIN_SESSIONS_FOR_OUTLIER);

  if (eligible.length >= 2) {
    const max = Math.max(...eligible.map((r) => r.fallbackRatePct));
    const min = Math.min(...eligible.map((r) => r.fallbackRatePct));

    if (max - min >= OUTLIER_SPREAD_THRESHOLD) {
      rows.forEach((r) => {
        if (r.total >= MIN_SESSIONS_FOR_OUTLIER && r.fallbackRatePct === max) {
          r.isOutlier = true;
        }
      });
    }
  }

  return rows.sort((a, b) => b.fallbackRatePct - a.fallbackRatePct);
}

@Component({
  selector: 'app-fallback-analytics-widget',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatTableModule,
  ],
  templateUrl: './fallback-analytics-widget.component.html',
  styleUrls: ['./fallback-analytics-widget.component.scss'],
})
export class FallbackAnalyticsWidgetComponent {
  private apiService = inject(ApiService);

  isLoading = signal(false);
  hasSearchedOnce = signal(false);
  errorMessage = signal<string | null>(null);

  timeRangeLabel = signal('');
  totalVoiceSessions = signal(0);
  totalFallbacks = signal(0);
  overallFallbackRatePct = signal(0);
  voiceOnlyRate = signal('0.0%');
  avgSessionDuration = signal('0s');
  avgTimeToFallback = signal('0s');
  overallKeepaliveRatePct = signal(0);

  byProductType = signal<BreakdownRow[]>([]);
  byBrand = signal<BreakdownRow[]>([]);
  byDate = signal<BreakdownRow[]>([]);
  fallbackDistribution = signal<DistributionRow[]>([]);
  insights = signal<Insight[]>([]);

  breakdownColumns = ['label', 'sessions', 'fallbacks', 'rate'];

  startDate: Date | null = null;
  endDate: Date | null = null;

  constructor() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    this.startDate = startDate;
    this.endDate = endDate;
  }

  hasSearched(): boolean {
    return this.hasSearchedOnce();
  }

  fetchOverview() {
    if (!this.startDate || !this.endDate) return;

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const endOfDay = new Date(this.endDate);
    endOfDay.setHours(23, 59, 59, 999);

    const queryParams: Record<string, string> = {
      startDate: this.startDate.toISOString(),
      endDate: endOfDay.toISOString(),
    };

    this.apiService
      .get<FallbackAnalyticsResponse | FallbackAnalyticsResponseBody>(
        'admin/get-fallback-analytics-overview',
        queryParams
      )
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const responseBody: FallbackAnalyticsResponseBody =
            'body' in response && response.body
              ? response.body
              : (response as FallbackAnalyticsResponseBody);

          const data = responseBody?.data;

          const overallFallbackRatePct = parsePercent(
            data?.fallbackMetrics?.fallbackRate
          );
          const overallKeepaliveRatePct = parsePercent(
            data?.qualityMetrics?.keepaliveRate
          );
          const byProductType = normalizeBreakdown(data?.productTypeBreakdown);
          const byBrand = normalizeBreakdown(data?.brandBreakdown);
          const byDate = normalizeBreakdown(data?.hourlyDistribution);

          const distribution = data?.fallbackDistribution;
          const distributionRows: DistributionRow[] = distribution
            ? (
                Object.keys(FALLBACK_DISTRIBUTION_LABELS) as Array<
                  keyof FallbackAnalyticsDistribution
                >
              )
                .map((key) => ({
                  label: FALLBACK_DISTRIBUTION_LABELS[key],
                  count: distribution[key] || 0,
                }))
                .filter((row) => row.count > 0)
                .sort((a, b) => b.count - a.count)
            : [];

          this.timeRangeLabel.set(
            data?.timeRange
              ? `Analyzed ${data.timeRange.daysAnalyzed} day(s) of data: ${this.formatDate(data.timeRange.startDate)} → ${this.formatDate(data.timeRange.endDate)}`
              : ''
          );
          this.totalVoiceSessions.set(data?.voiceSessions?.total || 0);
          this.totalFallbacks.set(data?.fallbackMetrics?.totalFallbacks || 0);
          this.overallFallbackRatePct.set(overallFallbackRatePct);
          this.voiceOnlyRate.set(
            data?.fallbackMetrics?.voiceOnlyRate || '0.0%'
          );
          this.avgSessionDuration.set(
            this.formatDuration(data?.voiceSessions?.avgDuration || 0)
          );
          this.avgTimeToFallback.set(
            this.formatDuration(data?.fallbackMetrics?.avgTimeToFallback || 0)
          );
          this.overallKeepaliveRatePct.set(overallKeepaliveRatePct);

          this.byProductType.set(byProductType);
          this.byBrand.set(byBrand);
          this.byDate.set(byDate);
          this.fallbackDistribution.set(distributionRows);

          this.insights.set(
            this.buildInsights(
              overallFallbackRatePct,
              overallKeepaliveRatePct,
              byProductType,
              byDate
            )
          );

          this.hasSearchedOnce.set(true);
          this.isLoading.set(false);
        },
        error: (err: HttpErrorResponse) => {
          console.error('Error fetching fallback analytics overview:', err);
          const message = err.error?.message || err.message || 'Unknown error';
          this.isLoading.set(false);
          this.errorMessage.set(
            `Failed to load fallback analytics. ${message}`
          );
        },
      });
  }

  private buildInsights(
    overallFallbackRatePct: number,
    overallKeepaliveRatePct: number,
    byProductType: BreakdownRow[],
    byDate: BreakdownRow[]
  ): Insight[] {
    const insights: Insight[] = [];

    if (overallFallbackRatePct > HIGH_FALLBACK_THRESHOLD) {
      if (overallKeepaliveRatePct > HIGH_KEEPALIVE_THRESHOLD) {
        insights.push({
          severity: 'warning',
          title: 'Voice quality issue (network gaps)',
          description: `Fallback rate is ${overallFallbackRatePct.toFixed(1)}% and keepalive rate is ${overallKeepaliveRatePct.toFixed(1)}%, suggesting connectivity drops are pushing customers to text.`,
          action: 'Check audio codec and latency.',
        });
      } else if (overallKeepaliveRatePct < LOW_KEEPALIVE_THRESHOLD) {
        insights.push({
          severity: 'info',
          title: 'UX issue (users prefer text)',
          description: `Fallback rate is ${overallFallbackRatePct.toFixed(1)}% with a low keepalive rate (${overallKeepaliveRatePct.toFixed(1)}%), suggesting customers are choosing to switch rather than being forced off by connectivity.`,
          action: 'Review voice prompt design and gather user feedback.',
        });
      }
    }

    const productOutlier = byProductType.find((r) => r.isOutlier);
    if (productOutlier) {
      const lowest = [...byProductType]
        .filter((r) => r.total >= MIN_SESSIONS_FOR_OUTLIER)
        .sort((a, b) => a.fallbackRatePct - b.fallbackRatePct)[0];
      insights.push({
        severity: 'info',
        title: `Product-specific high fallback: ${productOutlier.label}`,
        description: `${productOutlier.label} has a ${productOutlier.fallbackRatePct.toFixed(1)}% fallback rate${
          lowest
            ? ` vs ${lowest.fallbackRatePct.toFixed(1)}% for ${lowest.label}`
            : ''
        }.`,
        action: `Enhance the voice prompt for ${productOutlier.label}.`,
      });
    }

    const dateOutlier = byDate.find((r) => r.isOutlier);
    if (dateOutlier) {
      const eligible = byDate.filter(
        (r) => r.total >= MIN_SESSIONS_FOR_OUTLIER
      );
      const avg =
        eligible.reduce((sum, r) => sum + r.fallbackRatePct, 0) /
        (eligible.length || 1);
      insights.push({
        severity: 'warning',
        title: `Fallback rate spike: ${dateOutlier.label}`,
        description: `${dateOutlier.label} sees a ${dateOutlier.fallbackRatePct.toFixed(1)}% fallback rate vs an average of ${avg.toFixed(1)}%.`,
        action:
          'Check backend load and text agent availability around that time.',
      });
    }

    return insights;
  }

  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  }

  private formatDuration(milliseconds: number): string {
    if (!milliseconds) return '0s';

    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  }
}
